'use client'

import { memo, useEffect, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import {
  useNinxl8BitThemeMode,
  type Ninxl8BitTheme,
} from '@/app/(marketing)/_components/8bit'
import { NinOrgSceneLife } from './nin-org-scene-life'
import {
  NinOrgEraBackground,
  type ScenePalette,
} from './nin-org-era-background'

type PixelProps = {
  x: number
  y: number
  w?: number
  h?: number
  fill: string
}

const P = ({ x, y, w = 1, h = 1, fill }: PixelProps) => (
  <rect x={x * 4} y={y * 4} width={w * 4} height={h * 4} fill={fill} />
)

const SCENE_PALETTE: Record<Ninxl8BitTheme, ScenePalette> = {
  dark: {
    skyTop: '#05080c',
    skyMid: '#0a1520',
    skyLow: '#123040',
    star: '#ffffff',
    moon: '#d8e8f0',
    cloud: '#1a2838',
    hillA: '#0c1a22',
    hillB: '#142830',
    ground: '#121820',
    path: '#1a2830',
    pathEdge: '#0e1820',
    hut: '#1a2428',
    hutRoof: '#243038',
    field: '#0e1c18',
    foliage: '#0e2018',
    foliageDark: '#0a1812',
    stoneMid: '#243038',
    stoneLight: '#2a3840',
    neon: '#21728d',
    neonSoft: '#5eb8d0',
    tower: '#0a1018',
  },
  light: {
    skyTop: '#9ec8d8',
    skyMid: '#c5e0ea',
    skyLow: '#e8f4f8',
    star: '#ffffff',
    moon: '#fff8e0',
    cloud: '#d0e4f0',
    hillA: '#7a9aa8',
    hillB: '#8aadb8',
    ground: '#d0dde4',
    path: '#c0d0d8',
    pathEdge: '#a8bcc8',
    hut: '#98b0bc',
    hutRoof: '#5f7380',
    field: '#88a898',
    foliage: '#608050',
    foliageDark: '#4a6840',
    stoneMid: '#98b0bc',
    stoneLight: '#a8bcc8',
    neon: '#21728d',
    neonSoft: '#3a90a8',
    tower: '#5f7380',
  },
}

type TemplePalette = ScenePalette & {
  stone: string
  stoneDark: string
  ornament: string
  accent: string
  accentSoft: string
  lamp: string
  lampGlow: string
}

const TEMPLE_EXTRA: Record<Ninxl8BitTheme, Omit<TemplePalette, keyof ScenePalette>> = {
  dark: {
    stone: '#1e2830',
    stoneDark: '#141c24',
    ornament: '#3a5060',
    accent: '#21728d',
    accentSoft: '#3a90a8',
    lamp: '#21728d',
    lampGlow: '#5eb8d0',
  },
  light: {
    stone: '#88a1ac',
    stoneDark: '#5f7380',
    ornament: '#6a8898',
    accent: '#21728d',
    accentSoft: '#3a90a8',
    lamp: '#21728d',
    lampGlow: '#5eb8d0',
  },
}

const NicheDiya = ({
  x,
  y,
  p,
}: {
  x: number
  y: number
  p: TemplePalette
}) => (
  <>
    <P x={x} y={y} w={10} h={8} fill={p.stoneDark} />
    <P x={x + 1} y={y + 1} w={8} h={1} fill={p.ornament} />
    <P x={x + 1} y={y + 6} w={8} h={1} fill={p.ornament} />
    <P x={x + 3} y={y + 3} w={4} h={3} fill={p.lamp} />
    <P x={x + 4} y={y + 2} w={2} h={1} fill={p.lampGlow} />
  </>
)

const TempleStructure = memo(function TempleStructure({ p }: { p: TemplePalette }) {
  return (
    <svg
      viewBox="0 0 640 360"
      className="absolute inset-0 z-[2] h-full w-full"
      preserveAspectRatio="xMidYMid slice"
    >
      <P x={6} y={36} w={12} h={24} fill={p.stone} />
      <P x={6} y={36} w={12} h={2} fill={p.stoneLight} />
      <P x={8} y={32} w={8} h={4} fill={p.stoneMid} />
      <P x={10} y={30} w={4} h={2} fill={p.ornament} />
      <P x={11} y={28} w={2} h={2} fill={p.accent} />
      <P x={9} y={42} w={6} h={8} fill={p.stoneDark} />
      <P x={10} y={44} w={4} h={4} fill={p.lamp} />
      <P x={11} y={43} w={2} h={1} fill={p.lampGlow} />
      <P x={4} y={60} w={16} h={3} fill={p.stoneDark} />

      <P x={142} y={36} w={12} h={24} fill={p.stone} />
      <P x={142} y={36} w={12} h={2} fill={p.stoneLight} />
      <P x={144} y={32} w={8} h={4} fill={p.stoneMid} />
      <P x={146} y={30} w={4} h={2} fill={p.ornament} />
      <P x={147} y={28} w={2} h={2} fill={p.accent} />
      <P x={145} y={42} w={6} h={8} fill={p.stoneDark} />
      <P x={146} y={44} w={4} h={4} fill={p.lamp} />
      <P x={147} y={43} w={2} h={1} fill={p.lampGlow} />
      <P x={140} y={60} w={16} h={3} fill={p.stoneDark} />

      <P x={26} y={20} w={16} h={50} fill={p.stone} />
      <P x={26} y={20} w={16} h={2} fill={p.stoneLight} />
      <P x={26} y={68} w={16} h={2} fill={p.stoneDark} />
      <P x={26} y={22} w={1} h={46} fill={p.stoneDark} />
      <P x={41} y={22} w={1} h={46} fill={p.stoneLight} />
      <P x={24} y={18} w={20} h={3} fill={p.stoneMid} />
      <P x={23} y={16} w={22} h={2} fill={p.ornament} />
      <P x={24} y={70} w={20} h={3} fill={p.stoneDark} />
      <P x={22} y={73} w={24} h={3} fill={p.stone} />
      <P x={20} y={76} w={28} h={2} fill={p.stoneMid} />
      <P x={27} y={26} w={14} h={1} fill={p.accent} />
      <P x={27} y={38} w={14} h={1} fill={p.ornament} />
      <P x={27} y={50} w={14} h={1} fill={p.accent} />
      <P x={27} y={62} w={14} h={1} fill={p.ornament} />
      <NicheDiya x={29} y={28} p={p} />
      <NicheDiya x={29} y={40} p={p} />
      <NicheDiya x={29} y={52} p={p} />
      {[28, 30, 32, 34, 36, 38].map((y) => (
        <P key={`ll-${y}`} x={27} y={y} w={1} h={1} fill={p.accentSoft} />
      ))}

      <P x={118} y={20} w={16} h={50} fill={p.stone} />
      <P x={118} y={20} w={16} h={2} fill={p.stoneLight} />
      <P x={118} y={68} w={16} h={2} fill={p.stoneDark} />
      <P x={118} y={22} w={1} h={46} fill={p.stoneLight} />
      <P x={133} y={22} w={1} h={46} fill={p.stoneDark} />
      <P x={116} y={18} w={20} h={3} fill={p.stoneMid} />
      <P x={115} y={16} w={22} h={2} fill={p.ornament} />
      <P x={116} y={70} w={20} h={3} fill={p.stoneDark} />
      <P x={114} y={73} w={24} h={3} fill={p.stone} />
      <P x={112} y={76} w={28} h={2} fill={p.stoneMid} />
      <P x={119} y={26} w={14} h={1} fill={p.accent} />
      <P x={119} y={38} w={14} h={1} fill={p.ornament} />
      <P x={119} y={50} w={14} h={1} fill={p.accent} />
      <P x={119} y={62} w={14} h={1} fill={p.ornament} />
      <NicheDiya x={121} y={28} p={p} />
      <NicheDiya x={121} y={40} p={p} />
      <NicheDiya x={121} y={52} p={p} />
      {[28, 30, 32, 34, 36, 38].map((y) => (
        <P key={`rl-${y}`} x={132} y={y} w={1} h={1} fill={p.accentSoft} />
      ))}

      <P x={26} y={16} w={108} h={5} fill={p.stone} />
      <P x={26} y={16} w={108} h={2} fill={p.stoneLight} />
      <P x={26} y={20} w={108} h={1} fill={p.accent} />
      {[30, 38, 46, 54, 62, 70, 78, 86, 94, 102, 110, 118, 126].map((x) => (
        <P key={`lo-${x}`} x={x} y={18} w={2} h={2} fill={p.ornament} />
      ))}
      {[34, 50, 66, 82, 98, 114].map((x) => (
        <P key={`la-${x}`} x={x} y={18} w={2} h={2} fill={p.accent} />
      ))}

      <P x={34} y={12} w={92} h={4} fill={p.stoneDark} />
      <P x={34} y={12} w={92} h={1} fill={p.stoneLight} />
      <P x={42} y={8} w={76} h={4} fill={p.stone} />
      <P x={42} y={8} w={76} h={1} fill={p.stoneLight} />
      <P x={50} y={5} w={60} h={3} fill={p.stoneMid} />
      <P x={58} y={3} w={44} h={2} fill={p.ornament} />
      <P x={66} y={1} w={28} h={2} fill={p.stoneLight} />
      <P x={76} y={0} w={8} h={2} fill={p.accent} />
      <P x={78} y={0} w={4} h={1} fill={p.lampGlow} />

      <P x={42} y={21} w={76} h={2} fill={p.stoneDark} />
      <P x={46} y={23} w={68} h={2} fill={p.stoneDark} />
      <P x={50} y={25} w={60} h={2} fill={p.stoneDark} />
      <P x={54} y={27} w={52} h={1} fill={p.stoneMid} />

      {[56, 72, 88, 104].map((x) => (
        <g key={`bell-${x}`}>
          <P x={x} y={21} w={2} h={5} fill={p.ornament} />
          <P x={x - 1} y={26} w={4} h={3} fill={p.lamp} />
          <P x={x} y={25} w={2} h={1} fill={p.lampGlow} />
        </g>
      ))}

      <P x={48} y={78} w={64} h={3} fill={p.stoneDark} />
      <P x={46} y={81} w={68} h={3} fill={p.stone} />
      <P x={44} y={84} w={72} h={2} fill={p.stoneMid} />
      <P x={76} y={74} w={8} h={4} fill={p.stoneDark} />
      <P x={78} y={72} w={4} h={3} fill={p.lamp} />
      <P x={79} y={71} w={2} h={1} fill={p.lampGlow} />
      {[52, 60, 68, 76, 84, 92, 100].map((x) => (
        <P key={`pe-${x}`} x={x} y={79} w={2} h={1} fill={p.accent} />
      ))}
    </svg>
  )
})

/** baby: Match era-background cycle for labels */
const CYCLE_MS = 16000
const smooth = (t: number) => t * t * (3 - 2 * t)
const eraFromTime = (t: number): number => {
  if (t < 0.25) return 0
  if (t < 0.45) return smooth((t - 0.25) / 0.2)
  if (t < 0.7) return 1
  if (t < 0.9) return smooth(1 - (t - 0.7) / 0.2)
  return 0
}

/**
 * Temple sanctuary: fixed torana; background ages village → cyberpunk
 * by growing the same building slots (visible through the arch).
 */
export const NinOrgMissionScene = memo(function NinOrgMissionScene({
  className,
}: {
  className?: string
}) {
  const theme = useNinxl8BitThemeMode()
  const reduceMotion = useReducedMotion()
  const [era, setEra] = useState(0)

  const bgPalette = SCENE_PALETTE[theme]
  const templePalette: TemplePalette = {
    ...bgPalette,
    ...TEMPLE_EXTRA[theme],
  }

  useEffect(() => {
    if (reduceMotion) {
      setEra(0)
      return
    }
    let frameId = 0
    let start: number | null = null
    const tick = (now: number) => {
      if (start === null) start = now
      const t = ((now - start) % CYCLE_MS) / CYCLE_MS
      setEra(eraFromTime(t))
      frameId = requestAnimationFrame(tick)
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [reduceMotion])

  const isCyber = era > 0.5

  return (
    <div className={cn('nin-org-mission-scene absolute inset-0 overflow-hidden', className)} aria-hidden>
      <NinOrgEraBackground palette={bgPalette} />

      <div className="pointer-events-none absolute left-3 top-3 z-[1]">
        <span
          className={cn(
            'ninxl-8bit-label rounded-sm bg-black/50 px-1.5 py-0.5 text-[7px] tracking-[0.14em] md:text-[8px]',
            isCyber ? 'text-[color:var(--ninxl-brand-teal)]' : 'text-white/85',
          )}
        >
          {isCyber ? 'INDIA · NEXT' : 'INDIA · THEN'}
        </span>
      </div>

      <TempleStructure p={templePalette} />

      <div className="pointer-events-none absolute inset-y-[28%] left-[12%] z-10 flex flex-col justify-center gap-2 md:left-[14%]">
        <span className="ninxl-8bit-label rounded-sm bg-[color:var(--ninxl-theme-bg-frame)]/80 px-1.5 py-0.5 text-[7px] tracking-[0.12em] text-[color:var(--ninxl-theme-text-accent)] md:text-[9px]">
          USERS
        </span>
        <span className="ninxl-8bit-label rounded-sm bg-[color:var(--ninxl-theme-bg-frame)]/80 px-1.5 py-0.5 text-[7px] tracking-[0.12em] text-[color:var(--ninxl-theme-text)] md:text-[9px]">
          NINX
        </span>
      </div>
      <div className="pointer-events-none absolute inset-y-[28%] right-[12%] z-10 flex flex-col justify-center gap-2 md:right-[14%]">
        <span className="ninxl-8bit-label rounded-sm bg-[color:var(--ninxl-theme-bg-frame)]/80 px-1.5 py-0.5 text-[7px] tracking-[0.12em] text-[color:var(--ninxl-theme-text-accent)] md:text-[9px]">
          DATA
        </span>
        <span className="ninxl-8bit-label rounded-sm bg-[color:var(--ninxl-theme-bg-frame)]/80 px-1.5 py-0.5 text-[7px] tracking-[0.12em] text-[color:var(--ninxl-theme-text)] md:text-[9px]">
          NINGENIE
        </span>
      </div>

      <div className="absolute inset-0 z-[3]">
        <NinOrgSceneLife />
      </div>
    </div>
  )
})
